import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Heart, Scale, Briefcase, GraduationCap, CheckCircle2, Target, ArrowRight } from 'lucide-react';
import { services } from '@/data';
import { ServiceCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { springPresets, fadeInUp, staggerContainer } from '@/lib/motion';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib';
const categoryIcons: Record<string, React.ReactNode> = {
  psychologia: <Brain className="w-6 h-6" />,
  seksuologia: <Heart className="w-6 h-6" />,
  mediacje: <Scale className="w-6 h-6" />,
  biznes: <Briefcase className="w-6 h-6" />,
  szkolenia: <GraduationCap className="w-6 h-6" />
};
export default function Services() {
  const [activeTab, setActiveTab] = useState<'individual' | 'mediation' | 'business'>('individual');
  
  const individualServices = services.filter(service => 
    service.category === 'psychologia' || service.category === 'seksuologia'
  );
  
  const mediationServices = services.filter(service => 
    service.category === 'mediacje'
  );
  
  const businessServices = services.filter(service => 
    service.category === 'biznes' || service.category === 'szkolenia'
  );
  
  const currentServices = activeTab === 'individual' ? individualServices : 
                         activeTab === 'mediation' ? mediationServices : businessServices;
  
  return <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.THERAPY_OFFICE_6} alt="Gabinety" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/60 to-primary" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={springPresets.gentle} className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-serif mb-6">
              Kompleksowe Wsparcie i Rozwój
            </h1>
            <p className="text-xl md:text-2xl text-primary-foreground/80 leading-relaxed font-light">
              Łączę wiedzę z zakresu psychologii, seksuologii, prawa i biznesu, 
              by oferować rozwiązania oparte na konkretach, empatii i skuteczności.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp}>
              <span className="text-accent font-semibold tracking-widest uppercase text-sm mb-4 block">
                Moje Podejście
              </span>
              <h2 className="text-3xl md:text-4xl font-serif mb-8 text-foreground">
                Nauka w służbie autentyczności
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Moja filozofia pracy opiera się na przekonaniu, że prawdziwa zmiana wymaga połączenia naukowej precyzji z głębokim szacunkiem dla indywidualnej drogi każdego człowieka. Nie wierzę w uniwersalne recepty — wierzę w metody potwierdzone badaniami, które służą odkrywaniu i wzmacnianiu tego, co w każdym z nas najautentyczniejsze.
              </p>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Dowody naukowe jako fundament</h3>
                    <p className="text-muted-foreground">
                      Każda interwencja opiera się na metodach potwierdzonych badaniami empirycznymi, nie na intuicji czy modnych trendach.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                    <Target className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Indywidualizacja bez kompromisów</h3>
                    <p className="text-muted-foreground">
                      Uniwersalne metody dostosowuję do unikalnej sytuacji każdego klienta, zachowując rygor metodologiczny.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                    <CheckCircle2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Transparentność procesu</h3>
                    <p className="text-muted-foreground">
                      Klient rozumie, dlaczego stosuję określoną metodę i jak może śledzić własny postęp w terapii.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent">
                    <Target className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Etyka jako nienaruszalna granica</h3>
                    <p className="text-muted-foreground">
                      Skuteczność nigdy nie kosztem godności klienta — każda interwencja szanuje autonomię i wartości osoby.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={springPresets.gentle} className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video lg:aspect-square">
              <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2026791950538297344_warsztat_interaktywny_1.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Przestrzeń pracy" className="w-full h-[592px] object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Services Grid */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-serif mb-4">Obszary Specjalizacji</h2>
            <p className="text-muted-foreground">
              Każdy proces dopasowuję do indywidualnych potrzeb klienta, 
              wykorzystując interdyscyplinarne doświadczenie.
            </p>
          </div>

          {/* Service Type Switcher */}
          <div className="flex justify-center mb-12">
            <div className="bg-muted rounded-full p-1 flex">
              <button
                onClick={() => setActiveTab('individual')}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'individual'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Usługi indywidualne
              </button>
              <button
                onClick={() => setActiveTab('mediation')}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'mediation'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Mediacje
              </button>
              <button
                onClick={() => setActiveTab('business')}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'business'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Usługi dla firm
              </button>
            </div>
          </div>

          {/* Link to detailed page */}
          {activeTab !== 'mediation' && (
            <div className="text-center mb-8">
              <Link 
                to={activeTab === 'individual' ? ROUTE_PATHS.SERVICES_INDIVIDUAL : 
                    ROUTE_PATHS.SERVICES_BUSINESS}
                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium"
              >
                Zobacz pełną ofertę {activeTab === 'individual' ? 'indywidualną' : 'dla firm'}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
          
          <AnimatePresence mode="wait">
            <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 50, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -50, scale: 0.95 }}
            transition={{ 
              duration: 0.5, 
              ease: "easeInOut",
              staggerChildren: 0.1
            }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {currentServices.map((service, index) => <motion.div 
              key={service.id} 
              className="flex flex-col"
              initial={{ opacity: 0, y: 30, rotateX: -15 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.15,
                ease: "easeOut"
              }}
            >
                <ServiceCard service={service} />
                <div className="mt-4 p-6 bg-white/50 rounded-xl border border-border/50 space-y-4 flex-grow">
                  <div className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <span className="text-xs font-bold text-accent uppercase tracking-tighter">Dla kogo:</span>
                      <p className="text-sm text-muted-foreground leading-relaxed">{service.targetAudience}</p>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-border/50">
                    <ul className="space-y-2">
                      {service.benefits.slice(0, 3).map((benefit, idx) => <li key={idx} className="flex items-center gap-2 text-sm text-foreground/80">
                          <div className="w-1 h-1 rounded-full bg-accent" />
                          {benefit}
                        </li>)}
                    </ul>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-primary rounded-3xl p-12 text-center text-primary-foreground relative overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-accent/20 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-serif mb-6">
                Rozpocznijmy proces zmiany
              </h2>
              <p className="text-primary-foreground/70 max-w-xl mx-auto mb-10 text-lg">
                Zapraszam na konsultację wstępną, podczas której omówimy Twoje potrzeby 
                i dobierzemy odpowiednią formę wsparcia.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer" className="bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-4 rounded-full font-medium transition-all inline-flex items-center gap-2 group">
                  Umów wizytę
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}